/* eslint-disable @khanacademy/ts-no-error-suppressions */
/**
 * A clickable list of breadcrumbs.
 */

import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import Link from "@khanacademy/wonder-blocks-link";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {LabelLarge} from "@khanacademy/wonder-blocks-typography";
import {
    withMarkConversion,
    type ConversionName,
    type WithMarkConversionProps,
    type WithoutMarkConversion,
} from "@repo/analytics-learning-time";

export type SingleBreadcrumb = {
    title: string;
    href: string;
    onClick?: () => unknown;
    // If specified, will be passed to ClientLink
    forceServer?: boolean;
    contentId?: string;
};

export type BreadcrumbsPath = Array<SingleBreadcrumb>;

export type BreadcrumbsTheme = "light" | "dark" | "default";

type OwnProps = {
    // (a11y) Used to signify that the breadcrumbs should be marked as
    // "navigation" by a screen-reader.
    isNavigation: boolean;
    openInNewTab: boolean;
    // Specifies the colorway of the breadcrumb text, by default it's blue.
    theme: BreadcrumbsTheme;
    // Renderable object separating breadcrumb fragments (eg: ">", <Icon/>)
    separator: React.ReactNode;
    // An optional bigBingo conversion to generate if a link is clicked.
    bigBingoLinkConversion?: ConversionName;
    // An optional referrer string to include in the link for analytics.
    referrer?: string;
    // Aphrodite stylesheet that will only be applied to the Link component
    style?: any;
    // On mobile-sized screens, we only want to show the last item, and
    // have a "<" preceeding it. We do this with media queries instead
    // of conditional rendering in order to make server-side rendering
    // easier.
    lastItemStyle?: any;
    path: BreadcrumbsPath;
    // Aphrodite stylesheet to override default style
    separatorStyle?: any;
    // Aphrodite stylesheet that will be applied to the container of the
    // back arrow, Link, and the separator.
    containerStyle?: any;
    // identifier used for tests
    testId?: string;
};

type Props = OwnProps & WithMarkConversionProps;

type DefaultProps = {
    isNavigation: Props["isNavigation"];
    openInNewTab: Props["openInNewTab"];
    theme: Props["theme"];
    separator: Props["separator"];
};

/**
 * @deprecated Use Breadcrumbs from the wonder-blocks-breadcrumbs package instead.
 */
const DeprecatedBreadcrumbs = (
    props: Props,
): React.ReactElement<React.ComponentProps<"div">> => {
    const {
        containerStyle,
        isNavigation,
        openInNewTab,
        path,
        separator,
        separatorStyle,
        style,
        testId,
        theme,
    } = props;

    // TODO(alex): Make this a flat list of separators/links so that
    // the styles passed in can apply to each one individually. The
    // current nesting prevents us from changing horizontal padding.
    const breadcrumbs = path.map((pathItem, index) => {
        const linkTheme = {
            dark: localStyles.themeDark,
            light: localStyles.themeLight,
            default: null, // we don't override the link styles by default
        }[theme];

        const seperatorTheme = {
            dark: localStyles.themeDark,
            light: localStyles.themeLight,
            default: localStyles.separatorThemeDefault,
        }[theme];

        const onClick = () => {
            if (props.bigBingoLinkConversion) {
                props.markConversion({
                    id: props.bigBingoLinkConversion,
                    extra: {
                        module: props.referrer,
                        type: "breadcrumb",
                    },
                });
            }

            if (pathItem.onClick) {
                pathItem.onClick();
            }
        };

        return (
            <LabelLarge
                key={index}
                style={[localStyles.container, containerStyle]}
            >
                {!!index && (
                    <span
                        aria-hidden="true"
                        className={css(
                            seperatorTheme,
                            separatorStyle || localStyles.divider,
                        )}
                    >
                        {separator}
                    </span>
                )}
                <Link
                    target={openInNewTab ? "_blank" : undefined}
                    href={pathItem.href}
                    onClick={onClick}
                    testId={testId}
                    style={[
                        linkTheme,
                        style,
                        index === path.length - 1 && props.lastItemStyle,
                    ]}
                >
                    {pathItem.title}
                </Link>
            </LabelLarge>
        );
    });

    return (
        <div
            aria-label={t`breadcrumbs`}
            // @ts-expect-error - TS2322 - Type '"navigation" | null' is not assignable to type 'AriaRole | undefined'.
            role={isNavigation ? "navigation" : null}
        >
            {breadcrumbs}
        </div>
    );
};

DeprecatedBreadcrumbs.defaultProps = {
    isNavigation: false,
    openInNewTab: false,
    separator: "/",
    theme: "default",
} as DefaultProps;

const localStyles = StyleSheet.create({
    container: {
        display: "inline-flex",
        verticalAlign: "middle",
        alignItems: "center",
    },
    divider: {
        padding: "0 0.5em",
    },
    separatorThemeDefault: {
        color: color.offBlack,
    },
    themeDark: {
        // TODO: replace this with offBlack80 when it's available.
        color: "rgba(33,36,44,0.80)",
    },
    themeLight: {
        color: color.white64,
    },
});

type ExportProps = WithoutMarkConversion<
    JSX.LibraryManagedAttributes<
        typeof DeprecatedBreadcrumbs,
        React.ComponentProps<typeof DeprecatedBreadcrumbs>
    >
>;

/**
 * TODO(somewhatabstract): This file is not properly annotated for learning
 * time, but we've decided it's not currently a priority.
 */
export default withMarkConversion(
    DeprecatedBreadcrumbs,
) as React.ComponentType<ExportProps>;
